import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Eye, RefreshCw, Loader2, Mail, MousePointerClick, Users, X, UserPlus, Search, Image as ImageIcon, Plus, Beaker, Save, RotateCcw, FilePlus2 } from "lucide-react";
import { SectionEditor, HeroEditor, TrackManEditor, FooterEditor, ImageManager, CoursePickerModal, SubjectField, inputCls } from "./NLEditors";

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_META = {
  subscribed: { label: "Angemeldet", dot: "bg-green-500", cls: "text-green-400 border-green-500/40" },
  unsubscribed: { label: "Abgemeldet", dot: "bg-[#ff0033]", cls: "text-[#ff0033] border-[#ff0033]/40" },
  unknown: { label: "Keine Einwilligung", dot: "bg-neutral-400", cls: "text-neutral-400 border-white/20" },
};

const ContactList = ({ canManage }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", source: "" });

  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    api.get(`/admin/newsletter-contacts?${p.toString()}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [status, q]);

  const setContactStatus = async (c, newStatus) => {
    try {
      await api.put(`/admin/newsletter-contacts/${c.id}`, { status: newStatus });
      toast.success("Status aktualisiert");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addContact = async () => {
    if (!f.email.trim()) { toast.error("E-Mail-Adresse angeben"); return; }
    try {
      await api.post("/admin/newsletter-contacts", f);
      toast.success("Kontakt zum Newsletter angemeldet");
      setShowAdd(false); setF({ first_name: "", last_name: "", email: "", phone: "", source: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";
  const counts = data?.counts || {};

  return (
    <div className="mt-10" data-testid="nl-contacts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-heading text-xl uppercase tracking-tight text-white">Newsletter-Kontaktliste</h3>
        <button onClick={() => setShowAdd(true)} data-testid="nl-add-contact-btn" className="flex items-center gap-2 bg-[#ff0033] text-white px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest hover:bg-[#cc0029]"><UserPlus size={14} /> Kontakt anmelden</button>
      </div>
      <p className="text-neutral-500 text-xs mb-4 max-w-3xl">Zentrale Liste aller Kontakte aus Kursbuchungen, Kunden und dem Newsletter-Funnel (automatisch zusammengeführt, dedupliziert). Versendet wird ausschließlich an Kontakte mit Status „Angemeldet“. Abmeldungen bleiben dauerhaft gespeichert und werden durch neue Buchungen nicht zurückgesetzt.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[["", `Alle`], ["subscribed", `Angemeldet (${counts.subscribed ?? 0})`], ["unsubscribed", `Abgemeldet (${counts.unsubscribed ?? 0})`], ["unknown", `Ohne Einwilligung (${counts.unknown ?? 0})`]].map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`nl-filter-${k || "all"}`} className={`font-mono-lab text-[10px] uppercase tracking-widest px-3 py-2 border ${status === k ? "border-[#ff0033] text-white bg-[#ff0033]/10" : "border-white/15 text-neutral-400 hover:text-white"}`}>{l}</button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, E-Mail oder Telefon…" data-testid="nl-contact-search" className="bg-[#141414] border border-white/10 text-white text-sm pl-9 pr-3 py-2 w-64 outline-none focus:border-[#ff0033] placeholder:text-neutral-600" />
        </div>
      </div>

      {showAdd && (
        <div className="border border-[#ff0033]/50 bg-[#0b0b0b] p-5 mb-5" data-testid="nl-add-form">
          <p className="font-mono-lab text-[10px] tracking-widest text-[#ff0033] uppercase mb-3">+ Kontakt anmelden</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input placeholder="Vorname" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className={inputCls} data-testid="nl-add-first" />
            <input placeholder="Nachname" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className={inputCls} data-testid="nl-add-last" />
            <input type="email" placeholder="E-Mail-Adresse *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="nl-add-email" />
            <input placeholder="Telefonnummer (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="nl-add-phone" />
            <input placeholder="Quelle / Notiz (optional, z.B. mündliche Einwilligung vor Ort)" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid="nl-add-source" />
          </div>
          <p className="text-neutral-500 text-[11px] mt-2">Mit dem Speichern bestätigst Du, dass eine dokumentierte Newsletter-Einwilligung des Kontakts vorliegt.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={addContact} data-testid="nl-add-save" className="bg-[#ff0033] text-white px-5 py-2.5 font-mono-lab text-xs uppercase tracking-widest hover:bg-[#cc0029]">Zum Newsletter anmelden</button>
            <button onClick={() => setShowAdd(false)} className="border border-white/15 text-neutral-300 px-4 py-2.5 font-mono-lab text-xs uppercase">Abbrechen</button>
          </div>
        </div>
      )}

      {!data ? <div className="text-neutral-500 py-6 font-mono-lab text-sm">Lädt…</div> : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm min-w-[860px]">
            <thead><tr className="bg-[#111] border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest text-left">
              <th className="p-3">Name</th><th className="p-3">E-Mail</th><th className="p-3">Telefon</th><th className="p-3">Herkunft</th><th className="p-3">Newsletter-Status</th><th className="p-3">Anmeldung</th><th className="p-3">Abmeldung</th>
            </tr></thead>
            <tbody>
              {data.items.map((c) => {
                const m = STATUS_META[c.status] || STATUS_META.unknown;
                return (
                  <tr key={c.id} className="border-b border-white/5" data-testid={`nl-contact-${c.email}`}>
                    <td className="p-3 text-white">{(c.first_name || c.last_name) ? `${c.first_name} ${c.last_name}`.trim() : (c.name || "—")}</td>
                    <td className="p-3 text-neutral-300">{c.email}</td>
                    <td className="p-3 text-neutral-400">{c.phone || "—"}</td>
                    <td className="p-3 text-neutral-400 text-xs">{c.source || c.consent_source || "funnel"}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                        <select value={c.status || "unknown"} onChange={(e) => setContactStatus(c, e.target.value)} disabled={!canManage}
                          data-testid={`nl-status-${c.email}`}
                          className={`bg-[#141414] border text-xs px-2 py-1.5 outline-none ${m.cls}`}>
                          <option value="subscribed">Angemeldet</option>
                          <option value="unsubscribed">Abgemeldet</option>
                          <option value="unknown">Keine Einwilligung</option>
                        </select>
                      </span>
                    </td>
                    <td className="p-3 text-neutral-500 text-xs">{fmtD(c.subscribed_at || c.consent_at)}</td>
                    <td className="p-3 text-neutral-500 text-xs">{fmtD(c.unsubscribed_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.items.length === 0 && <p className="text-neutral-500 text-sm p-4">Keine Kontakte für diesen Filter.</p>}
        </div>
      )}
    </div>
  );
};

const editsOf = (p) => ({ subject: p.subject, sections: p.sections, trackman: p.trackman, hero: p.hero, footer: p.footer });

const NewsletterManager = ({ canSend = true }) => {
  const [items, setItems] = useState(null);
  const [preview, setPreview] = useState(null);
  const [images, setImages] = useState([]);
  const [viewHtml, setViewHtml] = useState(null);
  const [busy, setBusy] = useState(false);
  const [renderBusy, setRenderBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [tplBusy, setTplBusy] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dirty, setDirty] = useState(0);
  const previewRef = useRef(null);
  previewRef.current = preview;

  const load = () => api.get("/admin/newsletters").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  const loadImages = () => api.get("/admin/newsletter/images").then((r) => setImages(r.data)).catch(() => {});
  useEffect(() => { load(); loadImages(); }, []);

  const makePreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/newsletter/preview"); setPreview(data); loadImages(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const rerender = async (next) => {
    if (!next) return;
    setRenderBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/preview-render", editsOf(next));
      setPreview((p) => ({ ...p, html: data.html, subject: data.subject }));
    } catch (e) { toast.error(apiError(e)); } finally { setRenderBusy(false); }
  };

  // Live-Vorschau: ~1 s nach der letzten Änderung automatisch neu rendern
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => rerender(previewRef.current), 900);
    return () => clearTimeout(t);
  }, [dirty]);

  const patch = (upd) => { setPreview((p) => ({ ...p, ...upd })); setDirty((d) => d + 1); };

  const onImagesChanged = () => { loadImages(); if (previewRef.current) rerender(previewRef.current); };

  const updateSection = (idx, sec) => patch({ sections: preview.sections.map((s, i) => (i === idx ? sec : s)) });
  const removeSection = (idx) => patch({ sections: preview.sections.filter((_, i) => i !== idx) });
  const addSectionFromPicker = (sec) => {
    patch({ sections: [...(preview.sections || []), sec] });
    setShowPicker(false);
    toast.success(`„${sec.title}“ hinzugefügt`);
  };
  const addManualSection = () => {
    const key = `eigene-${Math.random().toString(36).slice(2, 6)}`;
    patch({
      sections: [...(preview.sections || []), {
        key, manual: true, title: "Neue Sektion", when: "", coach: "", price_line: "",
        spots_label: "", theme: "", text: "Hier Deinen Text eintragen.", checks: [],
        cta_label: "Mehr erfahren", cta_url_raw: "/kurse", image_url: "",
      }],
    });
    loadImages();
    toast.success("Freie Sektion hinzugefügt – Texte, Button-Ziel und Bild frei bearbeitbar");
  };

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(idx)); } catch { /* ignore */ }
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const secs = [...preview.sections];
    const [moved] = secs.splice(dragIdx, 1);
    secs.splice(idx, 0, moved);
    setDragIdx(idx);
    setPreview({ ...preview, sections: secs });
  };
  const onDragEnd = () => { if (dragIdx !== null) setDirty((d) => d + 1); setDragIdx(null); };

  const saveTemplate = async () => {
    setTplBusy(true);
    try {
      const { data } = await api.put("/admin/newsletter-template", editsOf(preview));
      toast.success(`Vorlage gespeichert (${data.sections} Sektionen) – Texte bleiben beim nächsten Newsletter erhalten`);
      setPreview((p) => ({ ...p, template_saved: true }));
      loadImages();
    } catch (e) { toast.error(apiError(e)); } finally { setTplBusy(false); }
  };

  const resetTemplate = async () => {
    if (!window.confirm("Alle gespeicherten Texte auf den Standard-Newsletter zurücksetzen? Bilder bleiben erhalten.")) return;
    setTplBusy(true);
    try {
      await api.delete("/admin/newsletter-template");
      toast.success("Auf Standardtexte zurückgesetzt");
      await makePreview();
    } catch (e) { toast.error(apiError(e)); } finally { setTplBusy(false); }
  };

  const send = async () => {
    if (!window.confirm(`Newsletter jetzt an ${preview.recipients_count} Empfänger senden?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { edits: editsOf(preview) });
      toast.success(`Versand gestartet an ${data.recipients} Empfänger`);
      setPreview(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const testSend = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/test-send", { edits: editsOf(preview) });
      if (data.ok) toast.success(`Testmail an ${data.to} gesendet`);
      else toast.error(`Test-Versand fehlgeschlagen (${data.delivery})`);
    } catch (e) { toast.error(apiError(e)); } finally { setTestBusy(false); }
  };

  const view = async (nl) => {
    try { const { data } = await api.get(`/admin/newsletters/${nl.id}/html`); setViewHtml(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const existingKeys = (preview?.sections || []).map((s) => s.key);
  const availableRemaining = (preview?.available_courses || []).filter(
    (c) => !(preview?.sections || []).some((s) => s.course_id === c.course_id || s.title === c.name)
  );
  // Bild-Slots: Server-Slots + noch nicht gespeicherte eigene Sektionen
  const imageSlots = [
    ...images,
    ...(preview?.sections || []).filter((s) => s.manual && !images.some((i) => i.key === s.key)).map((s) => ({
      key: s.key, label: s.title || s.key, group: "Eigene Sektion", custom: false,
      url: s.image || s.image_url || "", size: 0,
    })),
  ];

  return (
    <div data-testid="newsletter-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white flex items-center gap-3"><Mail size={22} className="text-[#ff0033]" /> Newsletter-Mails</h2>
        <div className="flex items-center gap-2">
          <button onClick={load} className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2.5 font-mono-lab text-xs uppercase tracking-widest"><RefreshCw size={14} /> Aktualisieren</button>
          {canSend && (
            <button onClick={makePreview} disabled={busy} data-testid="nl-preview-btn" className="flex items-center gap-2 bg-[#ff0033] text-white px-4 py-2.5 font-mono-lab text-xs uppercase tracking-widest hover:bg-[#cc0029] disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Wochen-Newsletter erstellen
            </button>
          )}
        </div>
      </div>
      <p className="text-neutral-400 text-sm mb-5 max-w-3xl">Der Wochen-Newsletter wird automatisch aus den aktuellen Kursdaten erstellt (nur buchbare Termine, echte Restplätze). Überschriften, Texte, Buttons, Bilder, TrackMan-Block und Fußzeile sind vollständig editierbar und können als Vorlage gespeichert werden.</p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => { setShowImages((v) => !v); if (!showImages) loadImages(); }} className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:text-[#ff0033] px-3 py-2 font-mono-lab text-[11px] uppercase tracking-widest" data-testid="nl-images-toggle">
          <ImageIcon size={13} /> Bilder verwalten {showImages ? "▲" : "▼"}
        </button>
        {images.length > 0 && (
          <span className="text-neutral-500 text-[11px] font-mono-lab">{images.filter((i) => i.custom).length}/{images.length} eigene Bilder aktiv</span>
        )}
      </div>

      {showImages && <ImageManager slots={imageSlots} onChanged={onImagesChanged} />}

      {preview && (
        <div className="border border-[#ff0033]/50 bg-[#0b0b0b] mb-6" data-testid="nl-preview-box">
          <div className="flex flex-wrap items-start justify-between gap-3 p-4 border-b border-white/10">
            <div className="flex-1 min-w-[260px]">
              <SubjectField value={preview.subject} onChange={(v) => patch({ subject: v })} />
              <p className="text-neutral-500 text-xs mt-2">Enthaltene Kurse: {preview.courses_found.join(", ") || "—"} · Empfänger mit Opt-in: {preview.recipients_count}
                {preview.template_saved && <span className="text-emerald-400"> · Vorlage aktiv</span>}
              </p>
              {(preview.courses_missing || []).length > 0 && (
                <div className="mt-1.5" data-testid="nl-missing">
                  {preview.courses_missing.map((m, i) => (
                    <p key={i} className="text-amber-400 text-xs">⚠ {m.section} fehlt: {m.reason}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 items-start">
              <button onClick={() => rerender(preview)} disabled={renderBusy} className="flex items-center gap-1 border border-white/15 text-neutral-300 hover:text-white px-3 py-2 font-mono-lab text-xs uppercase disabled:opacity-50" data-testid="nl-refresh-preview">
                {renderBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Vorschau
              </button>
              <button onClick={saveTemplate} disabled={tplBusy} data-testid="nl-save-template" className="flex items-center gap-1 border border-emerald-500/50 text-emerald-400 px-3 py-2 font-mono-lab text-xs uppercase hover:bg-emerald-500/10 disabled:opacity-50" title="Alle Texte dauerhaft als Vorlage speichern">
                {tplBusy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Als Vorlage speichern
              </button>
              <button onClick={resetTemplate} disabled={tplBusy} data-testid="nl-reset-template" className="flex items-center gap-1 border border-white/15 text-neutral-400 hover:text-white px-3 py-2 font-mono-lab text-xs uppercase disabled:opacity-50" title="Standardtexte wiederherstellen">
                <RotateCcw size={12} /> Standard
              </button>
              <button onClick={testSend} disabled={testBusy || !preview.sections?.length} data-testid="nl-test-send-btn" className="flex items-center gap-1 border border-[#ff0033]/50 text-[#ff0033] px-3 py-2 font-mono-lab text-xs uppercase hover:bg-[#ff0033]/10 disabled:opacity-50" title="Testmail nur an dein Admin-Konto senden">
                {testBusy ? <Loader2 size={12} className="animate-spin" /> : <Beaker size={12} />} Testmail an mich
              </button>
              <button onClick={() => setPreview(null)} className="border border-white/15 text-neutral-300 px-4 py-2 font-mono-lab text-xs uppercase">Schließen</button>
              <button onClick={send} disabled={busy || preview.recipients_count === 0} data-testid="nl-send-btn" className="flex items-center gap-2 bg-[#ff0033] text-white px-4 py-2 font-mono-lab text-xs uppercase hover:bg-[#cc0029] disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Jetzt an {preview.recipients_count} senden
              </button>
            </div>
          </div>

          <div className="p-4 border-b border-white/10 space-y-3" data-testid="nl-edit-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase">
                Alle Inhalte bearbeiten · Ziehen zum Sortieren · Vorschau aktualisiert automatisch
                {renderBusy && <span className="text-[#ff0033] ml-2">rendert…</span>}
              </p>
              <div className="flex gap-2">
                <button onClick={addManualSection} data-testid="nl-add-manual-btn" className="flex items-center gap-1 border border-white/20 text-neutral-200 px-3 py-2 font-mono-lab text-[10px] uppercase hover:border-[#ff0033] hover:text-white">
                  <FilePlus2 size={12} /> Freie Sektion
                </button>
                <button onClick={() => setShowPicker(true)} disabled={availableRemaining.length === 0} data-testid="nl-add-course-btn" className="flex items-center gap-1 border border-[#ff0033]/50 text-[#ff0033] px-3 py-2 font-mono-lab text-[10px] uppercase hover:bg-[#ff0033]/10 disabled:opacity-40" title={availableRemaining.length === 0 ? "Keine weiteren buchbaren Kurse" : `${availableRemaining.length} Kurse verfügbar`}>
                  <Plus size={12} /> Kurs hinzufügen {availableRemaining.length > 0 && <span className="opacity-60">({availableRemaining.length})</span>}
                </button>
              </div>
            </div>

            <HeroEditor hero={preview.hero || {}} onChange={(h) => patch({ hero: h })} />

            {(preview.sections || []).map((sec, idx) => (
              <div key={sec.key || idx} draggable onDragStart={onDragStart(idx)} onDragOver={onDragOver(idx)} onDragEnd={onDragEnd}
                className={`transition-opacity ${dragIdx === idx ? "opacity-50" : ""}`}>
                <SectionEditor sec={sec} onChange={(s) => updateSection(idx, s)} onRemove={() => removeSection(idx)} />
              </div>
            ))}

            {preview.trackman && <TrackManEditor tm={preview.trackman} onChange={(tm) => patch({ trackman: tm })} />}
            <FooterEditor footer={preview.footer || {}} onChange={(f) => patch({ footer: f })} />
          </div>

          <iframe title="Newsletter Vorschau" srcDoc={preview.html} className="w-full bg-black" style={{ height: 720, border: 0 }} data-testid="nl-preview-iframe" />
        </div>
      )}

      {!items ? <div className="text-neutral-500 py-8 font-mono-lab text-sm">Lädt…</div>
        : items.length === 0 ? <div className="text-neutral-500 py-8 font-mono-lab text-sm" data-testid="nl-empty">Noch keine Newsletter versendet.</div>
        : (
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="bg-[#111] border-b border-white/10 text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest text-left">
                <th className="p-3">Versendet</th><th className="p-3">Betreff</th><th className="p-3"><Users size={12} className="inline mr-1" />Empfänger</th>
                <th className="p-3"><Eye size={12} className="inline mr-1" />Geöffnet</th><th className="p-3"><MousePointerClick size={12} className="inline mr-1" />Geklickt</th>
                <th className="p-3">Status</th><th className="p-3 text-right">Ansicht</th>
              </tr></thead>
              <tbody>
                {items.map((n) => (
                  <tr key={n.id} className="border-b border-white/5" data-testid={`nl-row-${n.id}`}>
                    <td className="p-3 text-neutral-400 whitespace-nowrap">{fmtDT(n.created_at)}</td>
                    <td className="p-3 text-white max-w-[260px] truncate">{n.subject}</td>
                    <td className="p-3 text-neutral-300">{n.recipients}</td>
                    <td className="p-3 text-neutral-300">{n.opens} <span className="text-neutral-500 text-xs">({n.open_rate} %)</span></td>
                    <td className="p-3 text-neutral-300">{n.clicks} <span className="text-neutral-500 text-xs">({n.click_rate} %)</span></td>
                    <td className="p-3"><span className={`inline-block font-mono-lab text-[10px] uppercase px-2 py-1 border ${n.status === "sent" ? "text-emerald-400 border-emerald-500/40" : "text-amber-400 border-amber-400/40"}`}>{n.status === "sent" ? `gesendet (${n.sent})` : "wird gesendet…"}</span></td>
                    <td className="p-3 text-right">
                      <button onClick={() => view(n)} data-testid={`nl-view-${n.id}`} className="inline-flex items-center gap-1 border border-[#ff0033]/50 text-[#ff0033] font-mono-lab text-[10px] uppercase px-2.5 py-1.5 hover:bg-[#ff0033]/10"><Eye size={12} /> Ansehen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <ContactList canManage={true} />

      {showPicker && preview && (
        <CoursePickerModal available={availableRemaining} existingKeys={existingKeys} onPick={addSectionFromPicker} onClose={() => setShowPicker(false)} />
      )}

      {viewHtml && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="nl-view-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setViewHtml(null)} />
          <div className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <p className="text-white text-sm font-medium truncate pr-4">{viewHtml.subject}</p>
              <button onClick={() => setViewHtml(null)} className="text-neutral-400 hover:text-white"><X size={18} /></button>
            </div>
            <iframe title="Newsletter" srcDoc={viewHtml.html} className="w-full bg-black" style={{ height: "75vh", border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterManager;
